import React, { useEffect, useState } from 'react'
import Head from 'next/head'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import clsx from 'clsx'

import { HeaderBar, HeaderBarLeftAction } from '@wartek-id/header-bar'
import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button'
import { historyUtils } from '@wartek-id/fe-toolbox'

import QueryNotCompleteError from 'app/PelatihanMandiriPage/components/QueryNotCompleteError'
import {
  useGetReflection,
  usePostRefleksi,
} from 'api/microlearning/useAPIMateri'
import { GATrackEvent } from 'utils/googleAnalytics'
import { EmptyState, EmptyStateAction } from 'components/EmptyState'
import { WithLayoutListener } from 'components/Layout/Desktop/DesktopLayout'

import CloseIcon from 'assets/icons/close.svg'

const MINIMUM_REFLECTION_LENGTH = 50
const isNotOnlyWhitespaceAndMoreThanMinChars = (str: string) => {
  return str.trim().length > MINIMUM_REFLECTION_LENGTH
}

const RefleksiPage = (props) => {
  const { isDesktopScreenSize } = props
  const { query, back, isReady: isRouterReady } = useRouter()
  const {
    isSuccess,
    isFetchedAfterMount,
    isError,
    data: reflectionData,
  } = useGetReflection(query.modul as string, query.id as string)
  const [isQueryNotComplete, setIsQueryNotComplete] = useState(false)
  const [isTrackOpened, setIsTrackOpened] = React.useState(false)
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm({
    mode: 'onChange',
  })

  useEffect(() => {
    if (isRouterReady && !query.modul) {
      setIsQueryNotComplete(true)
    }
  }, [query, isRouterReady])

  const { mutateAsync, isLoading: isSubmittingForm } = usePostRefleksi(
    query.modul as string,
    query.id as string,
    {
      onSuccess: () => {
        back()
      },
    }
  )

  useEffect(() => {
    if (isSuccess && !isTrackOpened) {
      GATrackEvent({
        eventName: 'learning_activity',
        eventParams: {
          event_trigger: 'module_open_reflection',
          action_type: 'open_material_reflection',
          meta: JSON.stringify({
            material_id: query.id,
            module_id: query.modul,
          }),
        },
        sendDefaultParams: true,
      })
      setIsTrackOpened(true)
    }
  }, [isSuccess, isTrackOpened, query.id, query.modul])

  const onSubmit = async (data) => {
    GATrackEvent({
      eventName: 'learning_activity',
      eventParams: {
        event_trigger: 'reflection_save_reflection',
        action_type: 'save_material_reflection',
        meta: JSON.stringify({
          material_id: query.id,
          module_id: query.modul,
        }),
      },
      sendDefaultParams: true,
    })
    await mutateAsync({
      answer: data.refleksi,
    })
  }

  if (isQueryNotComplete) {
    return <QueryNotCompleteError />
  }

  if (isError) {
    return (
      <>
        <EmptyState
          title="Data gagal dimuat"
          description="Ada kesalahan pada sistem. Silakan coba beberapa saat lagi."
        >
          <EmptyStateAction onClick={() => window.location.reload()}>
            Muat Ulang
          </EmptyStateAction>
        </EmptyState>
      </>
    )
  }
  // if (isSuccess && isDesktop) {
  //   return (
  //     <DesktopLayout
  //       submenu={
  //         from === 'learning-series' ? (
  //           <ForYou isDesktop={isDesktop} currentId={menuId as string} />
  //         ) : (
  //           <TopicList isDesktop={isDesktop} currentId={menuId as string} />
  //         )
  //       }
  //     >
  //       <>
  //         <Head>
  //           <title>
  //             Pelatihan Mandiri | Refleksi materi {query.materi_name ?? ''}
  //           </title>
  //         </Head>
  //         <div className="flex flex-row">
  //           {query.from && (
  //             <Icon as="i" className="mr-2" onClick={back}>
  //               arrow_back
  //             </Icon>
  //           )}
  //           <div>
  //             <Text className="pb-1">REFLEKSI</Text>
  //             <Text className="pb-3" variant="heading-lg">
  //               {reflectionData?.data?.question}
  //             </Text>
  //           </div>
  //         </div>
  //         <form
  //           className="flex flex-col h-full px-4 pt-[18px] pb-[4.75rem]"
  //           onSubmit={handleSubmit(onSubmit)}
  //           style={{ height: 'calc(100vh - 15em' }}
  //         >
  //           <div className="relative flex-auto mt-3 z-10">
  //             <textarea
  //               className="block h-full w-full border border-subdued focus:ring-1 rounded-lg bg-white"
  //               name="refleksi"
  //               id="refleksi"
  //               cols={30}
  //               rows={1}
  //               defaultValue={reflectionData?.data?.answer}
  //               {...register('refleksi', {
  //                 required: true,
  //                 validate: isNotOnlyWhitespaceAndMoreThanMinChars,
  //               })}
  //               placeholder="Ketik Refleksi Anda di sini"
  //             />
  //           </div>
  //           <div className="left-0 p-4 w-full z-0">
  //             <Button
  //               disabled={!isValid}
  //               type="submit"
  //               className="block w-full h-[44px] leading-[2.75] bg-interactive-default font-bold text-center text-white rounded-lg focus:ring-1 disabled:bg-surface-disabled disabled:text-icon-disabled"
  //               isLoading={isSubmittingForm}
  //             >
  //               Simpan
  //             </Button>
  //           </div>
  //         </form>
  //       </>
  //     </DesktopLayout>
  //   )
  // }

  if (isSuccess && isFetchedAfterMount) {
    return (
      <WithLayoutListener isDesktop={isDesktopScreenSize}>
        <Head>
          <title>
            Pelatihan Mandiri | Refleksi materi {query.materi_name ?? ''}
          </title>
        </Head>
        <div className={clsx(isDesktopScreenSize && 'bg-white p-2 rounded-md')}>
          {isDesktopScreenSize && (
            <div>
              <Text className="p-4" variant="heading-md">
                Cerita Reflektif
              </Text>
              <hr />
            </div>
          )}
          <form
            className={clsx(
              'flex flex-col h-full px-4 pt-[18px]',
              isDesktopScreenSize ? 'pb-2' : 'pb-[4.75rem]'
            )}
            onSubmit={handleSubmit(onSubmit)}
            style={{
              height: `calc(100vh - ${isDesktopScreenSize ? '18' : '3.5'}rem`,
            }}
          >
            <div className="flex-shrink-0">
              <Text
                as="label"
                variant="heading-sm"
                htmlFor="refleksi"
                className="w-full mb-4"
              >
                {reflectionData?.data?.question}
              </Text>
            </div>
            <div className="relative flex-auto mt-3 z-10">
              <textarea
                className="block h-full w-full border border-subdued focus:ring-1 rounded-lg bg-white"
                name="refleksi"
                id="refleksi"
                cols={30}
                rows={1}
                defaultValue={reflectionData?.data?.answer}
                {...register('refleksi', {
                  required: true,
                  validate: {
                    minLength: (v: string) =>
                      isNotOnlyWhitespaceAndMoreThanMinChars(v) ||
                      `Jawaban harus minimal ${MINIMUM_REFLECTION_LENGTH} karakter`,
                  },
                })}
                placeholder="Ketik Refleksi Anda di sini (min. 50 karakter)"
              />
            </div>
            {errors.refleksi && (
              <div className="relative mt-3 z-10">
                <Text variant="helper-bold" color="critical" aria-live="polite">
                  {`${errors.refleksi?.message}`}
                </Text>
              </div>
            )}
            <div
              className={clsx(
                !isDesktopScreenSize && 'absolute bottom-0 p-4',
                'left-0 pt-4 w-full z-0'
              )}
            >
              <Button
                disabled={!isValid}
                type="submit"
                className="block w-full h-[44px] leading-[2.75] bg-interactive-default font-bold text-center text-white rounded-lg focus:ring-1 disabled:bg-surface-disabled disabled:text-icon-disabled"
                isLoading={isSubmittingForm}
              >
                Simpan
              </Button>
            </div>
          </form>
        </div>
      </WithLayoutListener>
    )
  }

  return null
}

const PageHeader = () => {
  const { back } = useRouter()

  return (
    <HeaderBar inverse fixed variant="default" className="z-10">
      <HeaderBarLeftAction
        icon={<CloseIcon className="stroke-current text-default" />}
        onClick={() => historyUtils.backToApp(back)}
      />
    </HeaderBar>
  )
}

RefleksiPage.layoutOpts = {
  hasHeader: true,
  className: 'text-default',
  header: PageHeader,
  enableDesktopLayout: true,
}

export default RefleksiPage
